import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect, useDispatch, useSelector, Provider } from 'react-redux';
import ReactDom from 'react-dom';
import { RootState } from 'redux/root.reducer';
import { useHistory, useLocation } from 'react-router-dom';
import { find } from 'lodash';
import {
  APP_URL,
  CFCProps,
  SCREEN_SIZE,
  MAP_DATA_SERVICE_DATA_SET
} from 'types';
import MapView from './components/map';
import ConfirmationPanel from './components/confirmationPanel';
import LiveAreaTag from './components/liveAreaTag';
import ShowRecentsToggle from './components/showRecentsToggle';
import { removePin, zoomToFeature } from 'components/Map/helper';
import {
  clearShape,
  setShape,
  setEnquiryAddress,
  clearEnquiryPresets,
  completedReturnFromEnquiryPresets,
  clearLastEnquiry,
  fetchLastEnquiryRequest,
  cloneExistingEnquiryById,
  selectChannelTestMode,
  setSandBoxTestingMode
} from 'redux/lodgeEnquiry';
import {
  addAlert,
  removeAlert,
  removeAlertByGroup
} from 'redux/alerts/actions';
import {
  clearGraphic,
  addGraphicFromGeoJson,
  addClonedShapeToMap,
  getCentroidFromGeoJson
} from './components/map/drawingFunction';
import PropTypes from 'prop-types';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { LodgeNewEnquiryPanel } from './components/lodgeNewEnquiryPanel';
import {
  queryFeatureLayerExtentStart,
  getGeocodeStart,
  clearGeocode
} from 'redux/map/actions';
import { useComponentMount } from 'utilities/hooks.util';
import { fetchResetLodgeEnquiry, resetEnquiryShape } from 'redux/enquiries';
import { selectScreenSize } from 'redux/session/selectors';
import { setBaseMap, setShowInterstitialPage } from 'redux/app/actions';
import {
  selectBaseMapTheme,
  selectBaseMap,
  selectShowInterstitialPage
} from 'redux/app/selectors';
import { configureStore as store } from 'redux/store';
import { InterstitialModal } from '../../components';
import { Idea16, ChevronRight16 } from '@carbon/icons-react';

const recentEnquiriesPolygonDarkTheme = {
  type: 'simple-fill',
  style: 'solid',
  color: 'rgba(0, 89, 254, 0.35);',
  outline: {
    color: 'rgba(0, 169, 230, 0.75)',
    width: 1
  }
};

const recentEnquiriesPolygonLightTheme = {
  type: 'simple-fill',
  style: 'solid',
  color: 'rgba(0, 0, 0, 0.25);',
  outline: {
    style: 'none'
  }
};

export const LodgeNewEnquiry: React.FC<Props & any> = (props: Props) => {
  const {
    drawedShape,
    presets,
    baseMapOptions,
    prePopulatedData,
    clonedEnquiry
  } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const channelTestMode = useSelector(selectChannelTestMode);
  const screenSize = useSelector(selectScreenSize);
  const baseMapTheme = useSelector(selectBaseMapTheme);
  const baseMapId = useSelector(selectBaseMap);
  const showInterstitialPage = useSelector(selectShowInterstitialPage);
  const [activateEnquiryForm, setActivateEnquiryForm] = useState<boolean>(
    location?.search.includes('clone')
  );
  const [activeTool, setActiveTool] = useState<string>('selector');
  const [toolExpanded, setToolExpanded] = useState<boolean>(true);
  const [hasCloneActivity, setHasCloneActivity] = useState<boolean>(() =>
    location.search.includes('clone')
  );
  const [isSandboxTestActive, setIsSandboxTestActive] = useState<boolean>(
    false
  );
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [showInterstitial, setShowInterstitial] = useState<boolean>(false);
  const [isAutoSubmit, setIsAutoSubmit] = useState<boolean>(false);

  const { loadedSketchViewModel } = window;
  const formRefs = useRef({
    form1: undefined,
    form2: undefined,
    form3: undefined,
    form4: undefined
  });
  const alertGroup = 'map-alert';
  const resetPresetsCache = useRef<boolean>(true);
  const extraWidgetsDiv = useRef<HTMLDivElement>();
  // SENT-2684: Track if pre-populated shape has been added to prevent re-triggering on basemap theme change
  const prePopulatedShapeAddedRef = useRef<boolean>(false);
  const cloneShapeAppliedRef = useRef<string | null>(null);

  const doActionHandler = () => {
    dispatch(setSandBoxTestingMode(undefined));
    dispatch(removeAlert('channel-test-mode'));
    if (queryParams.has('isSandboxTest')) {
      queryParams.delete('isSandboxTest');
      history.replace({ search: queryParams.toString() });
    }
    history.push(APP_URL.MANAGE_REFERRAL_CHANNEL);
  };

  const setRecentRenderer = (theme: string) => {
    const { loadedMap } = window;
    if (loadedMap) {
      const layer = loadedMap.findLayerById('recent-enquiries-layer');
      if (layer) {
        const renderer = {
          type: 'simple',
          symbol:
            theme === 'dark'
              ? recentEnquiriesPolygonDarkTheme
              : recentEnquiriesPolygonLightTheme
        };

        layer.renderer = renderer;
      }
    }
  };

  const getBaseMapTheme = useCallback(() => {
    const baseMapObj = find(baseMapOptions, { id: baseMapId });
    if (baseMapObj) {
      const theme = baseMapObj?.theme || 'light';
      dispatch(setBaseMap(baseMapId, theme));
    }
  }, [baseMapId, baseMapOptions, dispatch]);

  const notificationProps = {
    title: 'Sandbox testing mode',
    subtitle: ``,
    lowContrast: true,
    doActions: doActionHandler,
    actionButtonText: 'Exit and return to Sandbox configuration',
    hideCloseButton: true
  };

  useEffect(() => {
    if (prePopulatedData?.isAutoSubmit) {
      setIsAutoSubmit(true);
    }
  }, [prePopulatedData]);

  useEffect(() => {
    if (channelTestMode) {
      if (location?.search.search('isSandboxTest') === -1) {
        history.push({
          pathname: APP_URL.LODGE_NEW_ENQUIRY,
          search: '?isSandboxTest=true'
        });
      }

      dispatch(
        addAlert('SANDBOX_TEST_RUN', 'info', '', '', {
          id: 'channel-test-mode',
          withClose: false,
          type: 'inline',
          notificationProps
        })
      );
    } else {
      if (location?.search.search('isSandboxTest') !== -1) {
        dispatch(setSandBoxTestingMode({ isSandboxTest: true }));
      }
    }
    setIsSandboxTestActive(channelTestMode !== undefined);
  }, [dispatch, notificationProps, location, channelTestMode, history]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history?.location?.search);
    const enquiryId = queryParams.get('clone') || '';
    const shareId = queryParams.get('shareId') || '';

    if (enquiryId) {
      cloneShapeAppliedRef.current = null;
      dispatch(clearGeocode());
      dispatch(clearShape());
      dispatch(cloneExistingEnquiryById({ enquiryId, shareId }));
      dispatch(fetchLastEnquiryRequest());
      setHasCloneActivity(true);
    } else {
      cloneShapeAppliedRef.current = null;
      dispatch(fetchResetLodgeEnquiry());
      setActivateEnquiryForm(false);
      setShowConfirm(false);
      // cancelEnquiryHandler(true);
    }
  }, [dispatch, history]);

  useEffect(() => {
    if (loadedSketchViewModel) {
      loadedSketchViewModel.updateOnGraphicClick = !activateEnquiryForm;
    }
  }, [loadedSketchViewModel, activateEnquiryForm]);

  useEffect(() => {
    if (drawedShape) {
      setActivateEnquiryForm(true);
      setShowConfirm(true);
    }
  }, [drawedShape]);

  useEffect(() => {
    if (presets?.status === 'returning') {
      const { shape, shapeArea, address, formData } = presets;
      if (shape && shapeArea) {
        dispatch(setShape({ shape, area: shapeArea }));
      }
      if (address) {
        dispatch(setEnquiryAddress(address));
      }
      if (formData) {
        formRefs.current = formData;
      }
      dispatch(completedReturnFromEnquiryPresets());
    }

    if (presets?.status === 'initial') {
      resetPresetsCache.current = false;
      history.push(APP_URL.ENQUIRY_PRESETS);
    }
  }, [presets, dispatch, history]);

  /**
   * Add pre-populated shape from web launcher to the map.
   *
   * SENT-2684: This effect should only run ONCE when the shape is first added.
   * We use prePopulatedShapeAddedRef to prevent re-triggering when baseMapTheme changes
   * (e.g., when user switches basemaps), which would cause setShape to dispatch,
   * leading to onMapReady recreation and unintended form auto-submission.
   */
  useEffect(() => {
    // Only add shape if all conditions are met AND shape hasn't been added yet
    if (
      mapReady &&
      baseMapTheme &&
      prePopulatedData?.shape &&
      !prePopulatedShapeAddedRef.current
    ) {
      const { shape } = prePopulatedData;
      const {
        polygonInGeoJson,
        geometryArea,
        centroid,
        geometry
      } = addGraphicFromGeoJson(shape, baseMapTheme);
      dispatch(setShape({ shape: polygonInGeoJson, area: geometryArea }));
      dispatch(
        getGeocodeStart({
          ...centroid,
          datasets: MAP_DATA_SERVICE_DATA_SET.GEOCODEAU
        })
      );
      zoomToFeature(geometry.extent, 0, true);

      // Mark as added to prevent re-triggering on subsequent baseMapTheme changes
      prePopulatedShapeAddedRef.current = true;
    }
  }, [mapReady, baseMapTheme, prePopulatedData, dispatch]);

  /**
   * Apply cloned enquiry shape to the map (same pattern as prePopulatedData):
   * draw on map, zoom, set lodge shape, and live-geocode for address consistency.
   */
  useEffect(() => {
    const cloneId = new URLSearchParams(history.location.search).get('clone');

    if (
      !cloneId ||
      !mapReady ||
      !baseMapTheme ||
      !clonedEnquiry?.shape ||
      String(clonedEnquiry.id) !== cloneId ||
      cloneShapeAppliedRef.current === cloneId
    ) {
      return;
    }

    const areaSqKm = clonedEnquiry.shapeArea
      ? Number(clonedEnquiry.shapeArea) / 1_000_000
      : undefined;

    const parsed = addGraphicFromGeoJson(clonedEnquiry.shape, baseMapTheme);
    let shapeToStore = clonedEnquiry.shape;
    let areaToStore = areaSqKm;
    let centroid = null;

    if (parsed?.polygonInGeoJson && parsed?.centroid) {
      shapeToStore = parsed.polygonInGeoJson;
      areaToStore = parsed.geometryArea ?? areaSqKm;
      centroid = parsed.centroid;
      if (parsed.geometry?.extent) {
        zoomToFeature(parsed.geometry.extent, 0, true);
      }
    } else {
      addClonedShapeToMap(
        clonedEnquiry.shape,
        true,
        areaSqKm ?? 0,
        baseMapTheme
      );
      centroid = getCentroidFromGeoJson(clonedEnquiry.shape);
    }

    dispatch(setShape({ shape: shapeToStore, area: areaToStore }));

    if (centroid) {
      dispatch(
        getGeocodeStart({
          latitude: String(centroid.latitude),
          longitude: String(centroid.longitude),
          datasets: MAP_DATA_SERVICE_DATA_SET.GEOCODEAU
        })
      );
    }

    cloneShapeAppliedRef.current = cloneId;
  }, [
    mapReady,
    baseMapTheme,
    clonedEnquiry,
    history.location.search,
    dispatch
  ]);

  useEffect(() => {
    if (!baseMapTheme && baseMapOptions?.length > 0) {
      getBaseMapTheme();
    }
    mapReady && baseMapTheme && setRecentRenderer(baseMapTheme);
  }, [mapReady, baseMapTheme, baseMapOptions, getBaseMapTheme]);

  useEffect(() => {
    const { loadedView } = window;
    if (!extraWidgetsDiv.current && loadedView) {
      const extraWidgets = document.createElement('div');
      extraWidgets.className = 'extra-widgets';
      loadedView.ui.add(extraWidgets, 'bottom-right');
      extraWidgetsDiv.current = extraWidgets;
    }
  }, [mapReady]);

  // Render extra widgets into the map container
  useEffect(() => {
    if (extraWidgetsDiv.current) {
      ReactDom.render(
        <Provider store={store}>
          {[SCREEN_SIZE.MD, SCREEN_SIZE.SM].includes(screenSize) && (
            <ShowRecentsToggle />
          )}
          <LiveAreaTag
            activeTool={activeTool}
            isSmallScreen={screenSize === SCREEN_SIZE.SM}
          />
        </Provider>,
        extraWidgetsDiv.current
      );
    }
  }, [screenSize, activeTool]);

  const canShowInterstitialModal = localStorage.getItem('interstitial-modal');

  useEffect(() => {
    if (canShowInterstitialModal !== 'hide' && showInterstitialPage) {
      setShowInterstitial(true);
    }
  }, [canShowInterstitialModal, showInterstitialPage]);

  useComponentMount(() => {
    !activateEnquiryForm && dispatch(fetchLastEnquiryRequest());
    return () => {
      dispatch(clearLastEnquiry());
      dispatch(fetchResetLodgeEnquiry());
      dispatch(removeAlertByGroup('SANDBOX_TEST_RUN'));
      if (resetPresetsCache.current) {
        dispatch(clearEnquiryPresets());
      }
      // Clean up ReactDom.render
      if (extraWidgetsDiv.current) {
        ReactDom.unmountComponentAtNode(extraWidgetsDiv.current);
      }
    };
  });

  const onClickSelector = () => {
    const { loadedMap, loadedSketchViewModel } = window;

    if (loadedMap !== undefined && loadedSketchViewModel !== undefined) {
      const drawingLayer: __esri.GraphicsLayer = loadedMap.findLayerById(
        'drawing-layer'
      );
      if (drawingLayer) {
        if (drawingLayer.graphics.length === 0) {
          loadedSketchViewModel.cancel();
        } else {
          loadedSketchViewModel.complete();
        }
      } else {
        loadedSketchViewModel.delete();
      }
      setActiveTool('selector');
      setToolExpanded(true);
      dispatch(removeAlertByGroup(alertGroup));
    }
  };

  const onClickDrawPolygon = (type: string) => {
    const { loadedMap, loadedSketchViewModel, loadedView } = window;

    if (loadedMap !== undefined && loadedSketchViewModel !== undefined) {
      loadedView.closePopup();
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      clearGraphic(drawingLayer, loadedSketchViewModel);
      dispatch(clearShape());
      dispatch(clearEnquiryPresets());
      loadedSketchViewModel.create(type, {
        mode: type !== 'circle' ? 'click' : 'freehand'
      });
      setActiveTool(type);
      setToolExpanded(false);
      dispatch(removeAlertByGroup(alertGroup));
    }
  };

  const onClickClearDrawing = useCallback(() => {
    const { loadedMap, loadedSketchViewModel } = window;

    if (loadedMap !== undefined && loadedSketchViewModel !== undefined) {
      const drawingLayer = loadedMap.findLayerById('drawing-layer');
      clearGraphic(drawingLayer, loadedSketchViewModel);
      dispatch(clearShape());
      dispatch(clearEnquiryPresets());
      setActiveTool('selector');
      setToolExpanded(true);
      dispatch(removeAlertByGroup(alertGroup));
    }
  }, [dispatch]);

  const clearSavedRefs = () => {
    formRefs.current = {
      form1: undefined,
      form2: undefined,
      form3: undefined,
      form4: undefined
    };
  };

  const cancelEnquiryHandler = useCallback(
    (clearRefs: boolean) => {
      if (clearRefs) {
        clearSavedRefs();
        dispatch(fetchResetLodgeEnquiry());
      } else {
        dispatch(resetEnquiryShape());
      }
      onClickClearDrawing();
      setActivateEnquiryForm(false);
      setShowConfirm(false);
      if (hasCloneActivity) {
        history.push(APP_URL.LODGE_NEW_ENQUIRY);
        setHasCloneActivity(false);
      }
    },
    [onClickClearDrawing, hasCloneActivity, history, dispatch]
  );

  const completeEnquiryHandler = () => {
    const { mapOriginalExtent, loadedView } = window;
    refreshShowRecent();
    onClickClearDrawing();
    if (loadedView && mapOriginalExtent && !loadedView.destroyed) {
      loadedView.goTo(mapOriginalExtent).catch((error: any) => {
        if (error.name !== 'AbortError') {
          console.warn('Error zooming to original extent:', error);
        }
      });
    }
  };

  const onClickTools = (tool: string) => {
    removePin();
    switch (tool) {
      case 'selector': {
        onClickSelector();
        break;
      }
      case 'circle': {
        onClickDrawPolygon('circle');
        break;
      }
      case 'polyline': {
        onClickDrawPolygon('polyline');
        break;
      }
      case 'polygon': {
        onClickDrawPolygon('polygon');
        break;
      }
      case 'remove': {
        onClickClearDrawing();
        break;
      }
    }
  };

  const handleLodgeNewEnquiryGuideClick = () => {
    dispatch(setShowInterstitialPage(true));
    setShowInterstitial(true);
  };

  const refreshShowRecent = () => {
    const { loadedMap } = window;
    if (loadedMap) {
      const layer = loadedMap.findLayerById('recent-enquiries-layer');
      if (layer) {
        layer.refresh();
        dispatch(queryFeatureLayerExtentStart(layer));
      }
    }
  };

  const smallScreens = [SCREEN_SIZE.SM, SCREEN_SIZE.MD];
  return (
    <div
      id="lodgeNewEnquiry"
      data-testid="LodgeNewEnquiry"
      className={`lodgeNewEnquiry${!toolExpanded ? ' small-drawing-tool' : ''}${
        activateEnquiryForm ? ' form-activate' : ''
      }${isSandboxTestActive ? ' sandBoxTestingMode' : ''}${
        showConfirm ? ' draw-confirming' : ''
      }`}
      data-floating-menu-container
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading={
          !smallScreens.includes(screenSize) ? 'Lodge new enquiry' : ''
        }
        pageDescription=""
        enableHeaderGlobalBar={true}
        showAlternateHeaderBar={
          window.self !== window.top && screenSize === SCREEN_SIZE.SM
        }
        referralPage={location?.state}
        headerChildren={
          <>
            {activateEnquiryForm &&
              !(
                showConfirm &&
                screenSize === SCREEN_SIZE.SM &&
                !isAutoSubmit
              ) && (
                <LodgeNewEnquiryPanel
                  hasCloneActivity={hasCloneActivity}
                  formRefs={formRefs}
                  canAutoPresets={!presets?.status}
                  cancelEnquiryHandler={cancelEnquiryHandler}
                  completeEnquiryHandler={completeEnquiryHandler}
                />
              )}
          </>
        }
      >
        {!smallScreens.includes(screenSize) ? (
          <div
            className="lodge-new-enquiry-guide"
            onClick={handleLodgeNewEnquiryGuideClick}
          >
            <div className="idea-icon-container">
              <Idea16 />
            </div>
            <div>How to request plans for your job site</div>
            <div className="chevron-icon-container">
              <ChevronRight16 />
            </div>
          </div>
        ) : (
          <div className="lodge-new-enquiry-guide-sm-wrapper">
            <div>Lodge new enquiry</div>
            <div className="trigger" onClick={handleLodgeNewEnquiryGuideClick}>
              (tips)
            </div>
          </div>
        )}

        <>
          {showConfirm && screenSize === SCREEN_SIZE.SM && !isAutoSubmit && (
            <ConfirmationPanel
              cancelEnquiryHandler={cancelEnquiryHandler}
              confirmDraw={() => setShowConfirm(false)}
            />
          )}
          <MapView
            activeTool={activeTool}
            onClickTools={onClickTools}
            toolExpanded={toolExpanded}
            onExpandTools={() => setToolExpanded(true)}
            activateEnquiryForm={activateEnquiryForm}
            setMapReadyParent={setMapReady}
            baseMapTheme={baseMapTheme}
          />
        </>
      </FullHeaderLeftSideFullPanelLayout>
      <InterstitialModal
        isModalOpen={showInterstitial}
        onModalClose={() => setShowInterstitial(false)}
        screenSize={screenSize}
      />
    </div>
  );
};
const propTypes = {
  screenWidth: PropTypes.number
};

const mapStateToProps = ({
  lodgeEnquiryCache,
  enquiresCache,
  mapCache
}: RootState) => ({
  drawedShape: lodgeEnquiryCache.shape || enquiresCache?.data?.enquiry?.shape,
  clonedEnquiry: enquiresCache?.data?.enquiry,
  presets: lodgeEnquiryCache.presets,
  prePopulatedData: lodgeEnquiryCache.prePopulatedData,
  baseMapOptions: mapCache.basemapOptions
});

const connector = connect(mapStateToProps);

type ownProps = PropTypes.InferProps<typeof propTypes>;

type Props = CFCProps<typeof connector> & ownProps;

LodgeNewEnquiry.displayName = 'LodgeNewEnquiryModule';

export default React.memo(connector(LodgeNewEnquiry));
